// ============================================================
// CorrecaoDePresenca.js — o técnico pede a correção de uma presença
//
// Existia dentro da grade de presença diária, uma tela que saiu do ar
// quando a grade de duas linhas a substituiu. A função no servidor
// continuou publicada, com regra de permissão própria, e sem porta: das 95
// solicitações do núcleo, nenhuma tem origem "tecnico", porque não havia
// caminho até ela.
//
// Não é o mesmo que justificar falta. A justificativa vem do atleta e
// explica uma ausência; isto aqui é o técnico dizendo "esta pessoa estava
// no treino e o registro não entrou", que é outra coisa e acontece por
// celular sem bateria, aplicativo fechado, o de sempre.
//
// Quem pede não decide: o pedido vai para a coordenação mesmo quando quem
// pediu é o próprio técnico que decide as justificativas da modalidade.
// Está escrito no servidor, em decisoes.js, e repetido aqui para a tela não
// prometer o que a regra recusa.
// ============================================================

import { useState, useEffect } from "react";
import { carregarAtletas, criarSolicitacaoTecnico } from "../firebase";
import { normalizarModalidade } from "../calculo";
import { AZUL_ESCURO, CINZA_ESC, TEXTO, BRANCO, BORDA } from "../tema";
import Card from "./Card";
import Btn from "./Btn";
import StatusBox from "./StatusBox";

const estiloCampo = {
  width: "100%", border: "1px solid #D1D5DB", borderRadius: 8,
  padding: "9px 11px", fontSize: 13.5, fontFamily: "inherit",
  boxSizing: "border-box", color: TEXTO, background: BRANCO,
};

export default function CorrecaoDePresenca({ userNucleo, modalidade }) {
  const [aberto, setAberto] = useState(false);
  const [atletas, setAtletas] = useState([]);
  // Separado de "lista vazia": carregando e não tem ninguém são coisas
  // diferentes, e uma tela que diz "carregando" para sempre quando a
  // resposta foi "nenhum" é a que faz a pessoa ficar esperando.
  const [carregado, setCarregado] = useState(false);
  const [atleta, setAtleta] = useState("");
  const [data, setData] = useState("");
  const [justificativa, setJustificativa] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [status, setStatus] = useState(null);

  // A lista só é buscada quando o formulário abre. Fechado, ele é uma linha
  // de texto, e linha de texto não precisa consultar o cadastro.
  useEffect(() => {
    if (!aberto || !userNucleo || carregado) return;
    let vivo = true;
    carregarAtletas(userNucleo)
      .then((lista) => {
        if (!vivo) return;
        const daModalidade = (lista || []).filter((a) => (
          a.grupo === "ATLETA"
          && a.ativo !== false
          && (!modalidade || normalizarModalidade(a.modalidade) === normalizarModalidade(modalidade))
        ));
        setAtletas(daModalidade);
        setCarregado(true);
      })
      .catch(() => {
        if (!vivo) return;
        setCarregado(true);
        setStatus({ tipo: "erro", msg: "Não foi possível carregar os atletas." });
      });
    return () => { vivo = false; };
  }, [aberto, userNucleo, modalidade, carregado]);

  async function enviar() {
    if (!atleta || !data) {
      setStatus({ tipo: "erro", msg: "Escolha o atleta e o dia do treino." });
      return;
    }
    if (justificativa.trim().length < 5) {
      setStatus({ tipo: "erro", msg: "Escreva o que aconteceu. A coordenação decide lendo isto." });
      return;
    }
    setEnviando(true);
    setStatus(null);
    try {
      await criarSolicitacaoTecnico({ atletaUid: atleta, dataTreino: data, justificativa: justificativa.trim() });
      setStatus({ tipo: "ok", msg: "Pedido enviado à coordenação. Ele aparece na fila de solicitações dela." });
      setAtleta(""); setData(""); setJustificativa("");
    } catch (e) {
      setStatus({ tipo: "erro", msg: e && e.message ? e.message : "Não foi possível enviar." });
    } finally {
      setEnviando(false);
    }
  }

  if (!aberto) {
    return (
      <Card style={{ marginTop: 16 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 800, color: TEXTO }}>
              Faltou registrar a presença de alguém?
            </div>
          </div>
          <Btn onClick={() => setAberto(true)} style={{ fontSize: 13, padding: "9px 16px" }}>
            Pedir correção de presença
          </Btn>
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ marginTop: 16 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: TEXTO }}>
            Pedir correção de presença{modalidade ? ` · ${modalidade}` : ""}
          </div>
          <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 3, lineHeight: 1.55 }}>
            Para quem estava no treino e ficou sem registro. Não serve para justificar falta,
            que é pedido do atleta.
          </div>
        </div>
        <button onClick={() => { setAberto(false); setStatus(null); }}
          style={{ background: "none", border: `1px solid ${BORDA}`, borderRadius: 8, padding: "5px 11px", fontSize: 12, fontWeight: 600, color: TEXTO, cursor: "pointer", fontFamily: "inherit" }}>
          Fechar
        </button>
      </div>

      {status && <div style={{ marginBottom: 12 }}><StatusBox tipo={status.tipo} msg={status.msg} /></div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: CINZA_ESC, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 5 }}>Atleta</div>
          <select value={atleta} onChange={(e) => setAtleta(e.target.value)} style={{ ...estiloCampo, cursor: "pointer" }}>
            <option value="">— escolha o atleta —</option>
            {atletas.map((a) => <option key={a.uid} value={a.uid}>{a.nome}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, color: CINZA_ESC, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 5 }}>Dia do treino</div>
          <input type="date" value={data} onChange={(e) => setData(e.target.value)} style={estiloCampo} />
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 11, color: CINZA_ESC, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 5 }}>O que aconteceu</div>
        <input value={justificativa} onChange={(e) => setJustificativa(e.target.value)}
          placeholder="Ex: estava no treino, celular sem bateria na hora do registro."
          style={estiloCampo} />
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 14 }}>
        <Btn onClick={enviar} disabled={enviando || !atletas.length}>
          {enviando ? "Enviando…" : "Enviar para a coordenação"}
        </Btn>
        <span style={{ fontSize: 11.5, color: AZUL_ESCURO, lineHeight: 1.5 }}>
          Quem pede não decide: este pedido não entra na sua fila de justificativas.
        </span>
      </div>

      {!atletas.length && (
        <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 10 }}>
          {carregado
            ? `Nenhum atleta ativo${modalidade ? ` em ${modalidade}` : ""} para corrigir.`
            : "Carregando os atletas da modalidade…"}
        </div>
      )}
    </Card>
  );
}
